import {Component, computed, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FiscalJobDetail, FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {ExportTone} from '../_models/exports/data-export.models';
import {
  closingCountLabel,
  correctionErrorMessage,
  correctionItemReason,
  correctionItemStateLabel,
  correctionItemTone,
  correctionRunStateLabel,
  parseCorrectionReason
} from '../_models/closing-corrections/closing-correction.models';

/**
 * One correction run on screen (#771): when it was started, how far it got, and one line per closing with its
 * own outcome and its own retry.
 *
 * ### One line per closing, never an average
 *
 * Each closing is withdrawn and filed again on its own, and each can fail for its own reason: a changed VAT
 * rate stops it before anything is withdrawn, a conflict at the far end stops it after. "Two of three done" is
 * not something anybody can act on, while „Kasse 2, 03.09.2026: Fehlgeschlagen, erneut versuchen" is.
 *
 * ### The retry rule is the server's
 *
 * `item.retryable` is rendered as it arrives, like on the export screens (#770 review D3). The rule lives once,
 * in `FiscalJobStateMachine.IsRetryable`.
 *
 * ### Presentational
 *
 * No store, no request, no decision. The page owns which run is open and which retry is in flight, which is
 * also what lets Storybook render every outcome without a backend.
 */
@Component({
  selector: 'legal-closing-correction-run-card',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './closing-correction-run-card.component.html'
})
export class ClosingCorrectionRunCardComponent {
  /** The run, as the list read it. */
  readonly run = input.required<FiscalJobSummary>();

  /** The run with its entries when it is the opened one, otherwise `null`. */
  readonly detail = input<FiscalJobDetail | null>(null);

  /** Whether the entries are shown. */
  readonly expanded = input(false);

  /** The entry being retried right now, or `null`. */
  readonly retryingItemId = input<string | null>(null);

  /** The operator asked to open or close this run. Carries the run's id. */
  readonly toggled = output<string>();

  /** The operator asked for one closing to be tried again. */
  readonly retry = output<FiscalJobItem>();

  protected readonly stateLabel = computed(() => correctionRunStateLabel(this.run().state));

  /** „2 von 3 Kassenabschlüssen neu übermittelt" or, for one, „1 Kassenabschluss". */
  protected readonly progressLabel = computed(() => {
    const run = this.run();
    if (run.itemCount <= 1) return closingCountLabel(run.itemCount);
    return `${run.completedCount} von ${run.itemCount} Kassenabschlüssen neu übermittelt`;
  });

  /**
   * Why the run was started, off the summary's parameters, or `null`. On the collapsed header, so a list of
   * corrections reads as a list of reasons rather than of timestamps.
   */
  protected readonly reasonText = computed(() => parseCorrectionReason(this.run().parameters));

  protected readonly items = computed<FiscalJobItem[]>(() => this.detail()?.items ?? []);

  protected readonly panelId = computed(() => `correction-run-${this.run().id}`);

  /**
   * The run's own sentence, only when the run as a whole could not start. Keyed on the run's `errorCode` for
   * the reason the export card records (Bug #789): a run whose entries each failed is also `failed`, and its
   * reasons belong on the entries.
   */
  protected readonly runError = computed(() => {
    const code = this.run().errorCode;
    return this.run().state === 'failed' && code ? correctionErrorMessage(code) : null;
  });

  protected readonly running = computed(() =>
    this.run().state === 'queued' || this.run().state === 'running');

  protected label(item: FiscalJobItem): string {
    return item.label ?? 'Kassenabschluss';
  }

  protected itemLabel(item: FiscalJobItem): string {
    return correctionItemStateLabel(item.state);
  }

  protected tone(item: FiscalJobItem): ExportTone {
    return correctionItemTone(item.state);
  }

  /** Why an entry did not come out, in German, or `null` while it is fine or still running. */
  protected reason(item: FiscalJobItem): string | null {
    if (item.state === 'completed' || item.state === 'pending' || item.state === 'working') return null;
    return correctionItemReason(item);
  }
}
